import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { ChartContainer } from '@/components/ui/chart';

interface ImpactData {
  total_hours_saved: number;
  avg_hours_per_completed: number;
  top_modules_by_saving: Array<{
    module_key: string;
    minutes_saved: number;
  }>;
}

export function ImpactWidget() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<ImpactData | null>(null);
  const [loading, setLoading] = useState(true);

  // Formatera tal snyggt enligt svensk/engelsk standard
  const formatHours = (hours: number): string => {
    const locale = i18n.language === 'sv' ? 'sv-SE' : 'en-US';
    
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: hours >= 100 ? 0 : 1 // Inga decimaler för stora tal
    }).format(hours);
  };

  useEffect(() => {
    fetchImpactData();
  }, []);

  const fetchImpactData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('impact-summary', {
        body: { months: 12 }
      });
      
      if (error) throw error;
      setData(data);
    } catch (error) {
      console.error('Error fetching impact data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.impact.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 animate-pulse bg-muted rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.total_hours_saved === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.impact.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{t('dashboard.impact.noCompletedAudits')}</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.top_modules_by_saving.map(module => ({
    name: module.module_key.replace('_', ' '),
    value: Math.round(module.minutes_saved / 60 * 10) / 10
  }));

  const chartConfig = {
    value: {
      label: t('dashboard.impact.hours'),
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('dashboard.impact.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-2xl font-bold">
              {formatHours(data.total_hours_saved)} {t('dashboard.impact.hours')}
            </p>
            <p className="text-sm text-muted-foreground">{t('dashboard.impact.totalSaved')}</p>
          </div>
          <div>
            <p className="text-2xl font-bold">
              {formatHours(data.avg_hours_per_completed)} {t('dashboard.impact.hours')}
            </p>
            <p className="text-sm text-muted-foreground">{t('dashboard.impact.avgPerCompleted')}</p>
          </div>
        </div>
        
        {chartData.length > 0 && (
          <ChartContainer config={chartConfig} className="h-24 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="horizontal">
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} />
                <Tooltip />
                <Bar dataKey="value" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}